/**
 * Column Configuration for Matters DataTable
 *
 * Single source of truth for column IDs, categories, display names, and defaults.
 */

import {
  createColumnGrouper,
  createDisplayNameGetter,
} from '@/components/common/DataTable';

// Column IDs

export const MatterColumnIds = {
  FAVORITE: 'favorite',
  NAME: 'name',
  CREATED_AT: 'created_at',
  STATUS: 'status',
  STATUS_ENTERED_AT: 'status_entered_at',
  MATTER_TYPE: 'matter_type',
  DATE_OF_LOSS: 'date_of_loss',
  FIRM_NAME: 'firm_name',
  ATTORNEY: 'representing_attorney',
  PARALEGAL: 'managing_paralegal',
  CASE_MANAGER: 'case_manager',
  LAST_ACTIVITY_AT: 'last_activity_at',
  LAST_CONTACTED_AT: 'last_contacted_at',
  PREFERRED_LANGUAGE: 'preferred_language',
  SOL_STATUS: 'sol_status',
} as const;

// Column Categories

export const MATTER_COLUMN_CATEGORIES = {
  MATTER_INFO: 'Matter Info',
  DATES: 'Dates',
  PEOPLE: 'People',
} as const;

export type MatterColumnCategory =
  (typeof MATTER_COLUMN_CATEGORIES)[keyof typeof MATTER_COLUMN_CATEGORIES];

export const MATTER_COLUMN_CATEGORY_MAP: Record<string, MatterColumnCategory> =
  {
    [MatterColumnIds.NAME]: MATTER_COLUMN_CATEGORIES.MATTER_INFO,
    [MatterColumnIds.STATUS]: MATTER_COLUMN_CATEGORIES.MATTER_INFO,
    [MatterColumnIds.MATTER_TYPE]: MATTER_COLUMN_CATEGORIES.MATTER_INFO,
    [MatterColumnIds.FIRM_NAME]: MATTER_COLUMN_CATEGORIES.MATTER_INFO,
    [MatterColumnIds.PREFERRED_LANGUAGE]: MATTER_COLUMN_CATEGORIES.MATTER_INFO,
    [MatterColumnIds.CREATED_AT]: MATTER_COLUMN_CATEGORIES.DATES,
    [MatterColumnIds.DATE_OF_LOSS]: MATTER_COLUMN_CATEGORIES.DATES,
    [MatterColumnIds.STATUS_ENTERED_AT]: MATTER_COLUMN_CATEGORIES.DATES,
    [MatterColumnIds.LAST_ACTIVITY_AT]: MATTER_COLUMN_CATEGORIES.DATES,
    [MatterColumnIds.LAST_CONTACTED_AT]: MATTER_COLUMN_CATEGORIES.DATES,
    [MatterColumnIds.SOL_STATUS]: MATTER_COLUMN_CATEGORIES.DATES,
    [MatterColumnIds.ATTORNEY]: MATTER_COLUMN_CATEGORIES.PEOPLE,
    [MatterColumnIds.PARALEGAL]: MATTER_COLUMN_CATEGORIES.PEOPLE,
    [MatterColumnIds.CASE_MANAGER]: MATTER_COLUMN_CATEGORIES.PEOPLE,
  };

// Display Names

export const MATTER_COLUMN_DISPLAY_NAMES: Record<string, string> = {
  [MatterColumnIds.FAVORITE]: 'Favorite',
  [MatterColumnIds.NAME]: 'Matter Name',
  [MatterColumnIds.CREATED_AT]: 'Date Created',
  [MatterColumnIds.STATUS]: 'Case Status',
  [MatterColumnIds.STATUS_ENTERED_AT]: 'Time in Status',
  [MatterColumnIds.MATTER_TYPE]: 'Incident Type',
  [MatterColumnIds.DATE_OF_LOSS]: 'Date of Loss',
  [MatterColumnIds.FIRM_NAME]: 'Firm',
  [MatterColumnIds.ATTORNEY]: 'Attorney',
  [MatterColumnIds.PARALEGAL]: 'Managing Paralegal',
  [MatterColumnIds.CASE_MANAGER]: 'Case Manager',
  [MatterColumnIds.LAST_ACTIVITY_AT]: 'Last Task Update',
  [MatterColumnIds.LAST_CONTACTED_AT]: 'Last Client Update',
  [MatterColumnIds.PREFERRED_LANGUAGE]: 'Preferred Language',
  [MatterColumnIds.SOL_STATUS]: 'SOL Status',
};

// Display Configuration

export const MATTER_DISPLAY_CONFIG = {
  /** Days after which dates are highlighted as stale */
  staleDaysThreshold: 14,
} as const;

// Default Visible Columns

export const DEFAULT_MATTER_COLUMNS: readonly string[] = [
  MatterColumnIds.FAVORITE,
  MatterColumnIds.NAME,
  MatterColumnIds.CREATED_AT,
  MatterColumnIds.STATUS,
  MatterColumnIds.DATE_OF_LOSS,
  MatterColumnIds.FIRM_NAME,
  MatterColumnIds.PARALEGAL,
  MatterColumnIds.CASE_MANAGER,
  MatterColumnIds.LAST_ACTIVITY_AT,
  MatterColumnIds.LAST_CONTACTED_AT,
  MatterColumnIds.STATUS_ENTERED_AT,
  MatterColumnIds.MATTER_TYPE,
  MatterColumnIds.SOL_STATUS,
  MatterColumnIds.ATTORNEY,
  MatterColumnIds.PREFERRED_LANGUAGE,
] as const;

export function getDefaultMatterColumns(): string[] {
  return [...DEFAULT_MATTER_COLUMNS];
}

// Utility Functions

export const getMatterColumnDisplayName = createDisplayNameGetter(
  MATTER_COLUMN_DISPLAY_NAMES,
);

export function getMatterColumnCategory(
  columnId: string,
): MatterColumnCategory | undefined {
  return MATTER_COLUMN_CATEGORY_MAP[columnId];
}

export const groupMatterColumnsByCategory = createColumnGrouper(
  MATTER_COLUMN_CATEGORY_MAP,
  Object.values(MATTER_COLUMN_CATEGORIES),
);
